import '@/js/dist/www.iichi.com/common';

import { createApp } from 'vue';
import { lazyCreateApp } from '@/js/lib/helper/lazy-mount';
import { linkify } from '@/js/lib/helper/linkify';
import { addFollowButton, addFollowRecommendIfItemLiked } from '@/js/lib/ui/follow-button';
import SocialShare from '@/js/components/common/ui/share.vue';
import CartButton from '@/js/components/listing/CartButton.vue';
import MessageButton from '@/js/components/listing/MessageButton.vue';
import SameCategoryItems from '@/js/components/listing/SameCategoryItems.vue';
import SendRequestButton from '@/js/components/listing/SendRequestButton.vue';
import SendRequestFinished from '@/js/components/listing/SendRequestFinished.vue';
import PhotoViewer from '@/js/components/www.iichi.com/organisms/PhotoViewer.vue';
import FeaturedArticleLabel from '@/js/components/www.iichi.com/pages/listing/item/FeaturedArticleLabel.vue';
import LikeButton from '@/js/components/www.iichi.com/pages/listing/item/LikeButton.vue';
import ShippingPlans from '@/js/components/www.iichi.com/pages/listing/item/ShippingPlans.vue';

interface Picture {
  path: string;
  alt: string;
  width: number;
  height: number;
  comment: string;
}

document.addEventListener('DOMContentLoaded', () => {
  if ('customElements' in window) {
    const photoViewerEl = document.querySelector('photo-viewer') as HTMLElement;
    if (photoViewerEl) {
      const { pictures, hasThumbnail } = photoViewerEl.dataset;
      createApp(PhotoViewer, {
        pictures: JSON.parse(pictures!) as Picture[],
        hasThumbnail: hasThumbnail === 'true',
      }).mount(photoViewerEl);
    }
  }

  const sameCategoryItemsEl = document.querySelector('same-category-items') as HTMLElement;
  if (sameCategoryItemsEl) {
    const { categoryId, isVintage, price } = sameCategoryItemsEl.dataset;
    lazyCreateApp(sameCategoryItemsEl, SameCategoryItems, {
      categoryId: Number(categoryId),
      isVintage: isVintage === 'true',
      price: Number(price),
    });
  }

  if ('IntersectionObserver' in window) {
    const cartButtonEl = document.querySelector('.cart-button') as HTMLDivElement;
    const breadEl = document.querySelector('.breadcrumb') as HTMLDivElement;
    const footerMetaEl = document.querySelector('.footer-meta') as HTMLDivElement;
    const cartButtonObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          cartButtonEl.classList.toggle('fixed', !entry.isIntersecting);
        });
      },
      {
        rootMargin: '20px 0px 600px 0px',
      },
    );
    cartButtonObserver.observe(footerMetaEl || breadEl); // アプリの場合はパンくずを監視する
    cartButtonObserver.observe(cartButtonEl); // 「カートに入れる」ボタンを後に監視する必要がある
  }

  const cartButtonEl = document.querySelector('cart-button') as HTMLElement;
  if (cartButtonEl) {
    const { itemId, shopId, stock, hasOption, isOnVacation, isPreSaleNotStarted, buttonLabel } = cartButtonEl.dataset;
    createApp(CartButton, {
      itemId: Number(itemId),
      shopId: Number(shopId),
      stock: Number(stock),
      hasOption: Number(hasOption) as 1 | 0,
      isOnVacation: Number(isOnVacation) as 1 | 0,
      isPreSaleNotStarted: Boolean(Number(isPreSaleNotStarted) as 1 | 0),
      buttonLabel,
    }).mount(cartButtonEl);
  }

  const socialShareEl = document.querySelector('social-share') as HTMLElement;
  if (socialShareEl) {
    const { title, url } = socialShareEl.dataset;
    createApp(SocialShare, {
      title,
      url,
    }).mount(socialShareEl);
  }

  const messageButtonEl = document.querySelector('message-button') as HTMLElement;
  if (messageButtonEl) {
    const { disabled, itemId, shopId, subject } = messageButtonEl.dataset;
    createApp(MessageButton, {
      disabled: disabled === 'true',
      itemId: Number(itemId),
      shopId: Number(shopId),
      subject,
    }).mount(messageButtonEl);
  }

  const descriptionEl = document.querySelector('.item-description .text') as HTMLParagraphElement;
  descriptionEl.innerHTML = linkify(descriptionEl.innerHTML);

  const shippingPlansDetailsEl = document.querySelector('details.shipping-plans') as HTMLDetailsElement;
  const shippingPlansEl = document.querySelector('shipping-plans') as HTMLElement;
  let isShippingMethodFirstOpen = false;

  if (shippingPlansDetailsEl && shippingPlansEl) {
    shippingPlansDetailsEl.addEventListener('toggle', () => {
      if (!isShippingMethodFirstOpen && shippingPlansDetailsEl.open) {
        createApp(ShippingPlans, {
          itemId: Number(shippingPlansEl.dataset.itemId),
        }).mount(shippingPlansEl);
        isShippingMethodFirstOpen = true;
      }
    });
  }

  const likeButtonEl = document.querySelector('.item-like like-button') as HTMLElement;
  if (likeButtonEl) {
    createApp(LikeButton, {
      itemId: Number(likeButtonEl.dataset.itemId),
    }).mount(likeButtonEl);
  }

  addFollowButton().then(addFollowRecommendIfItemLiked);

  const sendRequestButtonEl = document.querySelector('send-request-button') as HTMLElement;
  if (sendRequestButtonEl) {
    const { itemId, shopId, isMe, isSubscribed } = sendRequestButtonEl.dataset;
    createApp(SendRequestButton, {
      itemId: Number(itemId),
      shopId: Number(shopId),
      isMe: Number(isMe),
      isSubscribed: Number(isSubscribed),
    }).mount(sendRequestButtonEl);
  }

  const sendRequestFinishedEl = document.querySelector('send-request-finished');
  if (sendRequestFinishedEl) {
    createApp(SendRequestFinished).mount(sendRequestFinishedEl);
  }

  const featuredArticleLabelEl = document.querySelector('featured-article-label') as HTMLElement;
  if (featuredArticleLabelEl) {
    const { itemId, apiKey } = featuredArticleLabelEl.dataset;
    createApp(FeaturedArticleLabel, {
      itemId: Number(itemId),
      apiKey: apiKey!,
    }).mount(featuredArticleLabelEl);
  }
});
